/**
 * Formats a float value to a localized French string with a specified number of decimal places.
 * If the value is `0`, it returns `0` instead of formatting it.
 *
 * @param {number|string} value - The numeric value to format.
 * @param {number} [decimals=2] - The number of decimal places (default is 2).
 * @returns {string|number} - The formatted number as a string, or `0` if the input is `0`.
 */
export function formatFloat(value, decimals = 2) {
  if (value === 0) {
    return value;
  }

  return parseFloat(value).toLocaleString("fr", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

/**
 * Formats a number to a localized French string using `toLocaleString()`.
 * If the input is not a number, it returns `false`.
 *
 * @param {number} value - The numeric value to format.
 * @returns {string|false} - The formatted number as a string, or `false` if the input is not a valid number.
 */
export function formatNumber(value) {
  return typeof value === "number" && value.toLocaleString("fr");
}

/**
 * Extracts the content inside the last pair of parentheses in a given string.
 * If no parentheses are found, returns the original input string.
 *
 * @param {string} input - The input string.
 * @returns {string} The content inside the last parentheses or the original string if no parentheses are found.
 */
export function extractLastParenthesisContent(input) {
  if (!input) {
    return input;
  }
  
  const match = input.match(/\(([^()]*)\)[^()]*$/);
  return match ? match[1] : input;
}